import type { FieldDef, ObjectMetadata } from '@olytic/foundation';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import { FigureImage } from '../../research/ResearchFiles';
import { asDateTime, column, createdByField, field, lastModifiedByField, lookup, related, type Row, text } from './kit';
import { yesNo } from './research-sources';

const def = OBJECT_DEFS['research-figures'];

/** The chart itself, fetched from its image document. */
const image: FieldDef<Row> = {
  key: 'image',
  label: 'Image',
  wide: true,
  render: (row) =>
    typeof row.image_document_id === 'string' && row.image_document_id !== ''
      ? <FigureImage id={row.image_document_id} alt={text(row.name)} />
      : '—',
};

/** A chart or infographic from a research source (0095). Rationale: research-figures.md. */
export const researchFigures: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Research Figure',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('title', 'Title'),
    field('source_name', 'Source', { link: lookup('research-sources', 'source_id') }),
    field('page_number', 'Page Number'),
    field('description', 'Description', { wide: true, help: 'What the figure shows, in words, so it can be searched.' }),
    image,
    field('image_document_name', 'Image Document', { link: lookup('documents', 'image_document_id') }),
    field('created_at', 'Created', { format: asDateTime }),
    field('updated_at', 'Last Modified', { format: asDateTime }),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['title', 'source_name', 'page_number', 'description'] },
    { key: 'figure', label: 'Figure', fields: ['image'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['image_document_name', 'created_at', 'created_by_name', 'updated_at', 'last_modified_by_name'] },
  ],
  related: [
    related('research-findings', 'Findings Citing It', 'research-findings', [
      column('name', 'Statement', { primary: true }),
      column('page_number', 'Page', { align: 'right' }),
      column('quote_verified', 'Quote Verified', { format: yesNo }),
    ]),
  ],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'source_name', 'description'],
};
